import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  KindEnum,
  NotFoundException,
  ReturnFieldEnum,
  TemplateEntity,
  TextFieldValue,
  WbGeneralPurposeEntityPicker,
} from '../../WbGeneralPurposeEntityPicker';
import { PolicyHandlerProvider } from './PolicyProvider/PolicyProvider';
import { useLatest } from 'react-use';
import { ApolloClient, useApolloClient } from '@apollo/client';
import { useApi } from '@mainstage/core-plugin-api';
import { customAlertApiRef } from '../../../apis';
import { areArraysEqual } from '@agilelab/plugin-wb-platform-common';

type PolicyPickerProps = {
  title: string;
  description?: string | undefined;
  multiSelection?: boolean;
  freeSolo?: boolean;
  id?: string;
  onSelect: (val: any | undefined) => void;
  value: any | undefined;
  required?: boolean;
  disabled?: boolean;
  filters?: Record<string, any>;
  defaultValue?: any[];
};

export const entityHandler = (
  apolloClient: ApolloClient<any>,
  availableKinds: TemplateEntity[],
) =>
  new Map([
    [KindEnum.policy, new PolicyHandlerProvider(apolloClient, availableKinds)],
  ]);

/**
 * PolicyPicker — Entity picker specialized for Policy entities.
 *
 * Workflow:
 * 1. When `value` or `defaultValue` changes, `loadInitialValue()` resolves them from the backend
 * 2. Resolved values are stored in `resolvedValues` state and passed to the picker for hydration
 * 3. User selections update `currentValue` and trigger `onSelect()` callback
 * 4. Dialog state persists for re-opening at the same selection
 *
 * The `resolvedValues` prop is essential for multi-step forms where hydration
 * across step transitions must preserve pre-loaded entity data.
 */
export const PolicyPicker = ({
  title,
  description,
  required,
  disabled,
  freeSolo = false,
  onSelect,
  filters,
  value,
  defaultValue,
}: PolicyPickerProps) => {
  const onChangeRef = useLatest(onSelect);
  const alertApi = useApi(customAlertApiRef);

  const apolloClient = useApolloClient();

  const availableKinds: TemplateEntity[] = useMemo(() => {
    return [
      {
        type: KindEnum.policy,
        returnField: ReturnFieldEnum.full,
        userFilters: ['search', 'status', 'severity'],
        filter: filters ?? undefined,
      },
    ];
  }, [filters]);

  const providerMapper = useMemo(
    () => entityHandler(apolloClient, availableKinds),
    [apolloClient, availableKinds],
  );
  const [currentValue, setCurrentValue] = useState<TextFieldValue[]>([]);
  const currentValueRef = useLatest(currentValue);

  useEffect(() => {
    if (value === '' || value === undefined || value === null) {
      setCurrentValue([]);
      return;
    }
  }, [value]);

  // Resolve a single entity reference via the appropriate provider.
  // Returns undefined if the entity is not found or reference is invalid.
  const fetchData = useCallback(
    async (ref: string) => {
      for (const [key, provider] of Array.from(providerMapper.entries())) {
        if (availableKinds.some(a => a.type === key)) {
          try {
            const res = await provider.resolve(ref);

            if (res) {
              return res;
            }
          } catch (err) {
            if (err instanceof NotFoundException) {
              return undefined;
            }

            alertApi.post({
              error: err,
              severity: 'error',
            });
            return undefined;
          }
        }
      }

      return undefined;
    },
    [alertApi, availableKinds, providerMapper],
  );

  // Transform an array of refs/IDs into TextFieldValue objects.
  // This hydrates the picker state when the component mounts or when value/defaultValue changes.
  // Each value is first checked in cache (currentValue), then fetched from backend.
  const loadInitialValue = useCallback(
    (initialValues: string[]) => {
      const checkCurrentValue = (val: string) => {
        const justResolved = currentValueRef.current?.find(
          cv => cv.value === val,
        );
        return justResolved ? justResolved : null;
      };

      return initialValues.map(async input => {
        const justResolved = checkCurrentValue(input);
        if (justResolved) {
          return justResolved;
        }

        const entity = await fetchData(input);
        return entity
          ? entity
          : {
              kind: KindEnum.arbitrary,
              label: input,
              value: input,
            };
      });
    },
    [currentValueRef, fetchData],
  );

  // Update local state and propagate the selected value(s) to the parent form.
  const handleSelect = useCallback(
    (values: TextFieldValue[] | undefined) => {
      if (!values && currentValue.length === 0) return;
      if (values && areArraysEqual(values, currentValue)) return;

      setCurrentValue(values ?? []);
      const formDataValues = values?.map(v => v?.value);
      onChangeRef.current(formDataValues?.[0]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChangeRef],
  );

  const [resolvedValues, setResolvedValues] = useState<
    TextFieldValue[] | undefined
  >(undefined);

  const hasResolved = useRef(false);

  // On mount, resolve initial values from `value` or `defaultValue` props.
  // These are transformed into TextFieldValue objects and passed to the picker context
  // for proper hydration and display on subsequent dialog opens.
  // The hasResolved flag ensures this effect runs only once per component lifecycle.
  useEffect(() => {
    if (hasResolved.current) return;

    let valuesFromValue: (typeof value)[] = [];
    if (value) {
      valuesFromValue = Array.isArray(value) ? value : [value];
    }

    const initialValues = [...(defaultValue ?? []), ...valuesFromValue];
    if (!initialValues.length) return;

    hasResolved.current = true;

    const resolveValues = async () => {
      const resolved = await Promise.all(loadInitialValue(initialValues));
      setResolvedValues(resolved);
    };

    resolveValues();
  }, [defaultValue, value, loadInitialValue]);

  return (
    <WbGeneralPurposeEntityPicker
      title={title}
      description={description}
      availableKinds={availableKinds}
      freeSolo={freeSolo}
      required={required}
      disabled={disabled}
      providerMapper={providerMapper}
      onSelect={handleSelect}
      value={currentValue ?? []}
      // resolvedValues supplies pre-resolved entities for picker hydration on dialog open.
      // The context splits them into tableValues (from catalog) and arbitraryValues (custom).
      resolvedValues={resolvedValues}
    />
  );
};
